// FlixDetailList.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { InfiniteScroller, ScrollItem } from './InfiniteScroller.jsx';
import { CustomImpression } from '../resource/impressionConstants.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { freezoneFeed } from '../resource/freezoneConstants.js';
import { useInView } from 'react-intersection-observer';
import HydrationBoundary from '../component/HydrationBoundary.jsx';
import FlixListItem from '../container/FlixListItem.js';
import { withTranslation } from 'react-i18next';
import { breakpoint } from '../style/variables.js';

import Loading from '../component/Loading.jsx';

const isServer = typeof window === 'undefined';

export class FlixDetailList extends React.PureComponent {
  state = {
    messageIds: [],
  };
  constructor(props) {
    super(props);
    const { messageIds } = this.props;
    if ((isServer || !window.__IS_HYDRATED__) && messageIds.length) {
      this.state.messageIds = messageIds;
    }
  }
  componentDidMount() {
    const { listType, isFirstPageFetched } = this.props;
    if (window.__IS_HYDRATED__ || !isFirstPageFetched) {
      this.timeout = setTimeout(() => {
        const { messageIds } = this.props;
        this.setState({ messageIds });
        this.fetchMessages({ type: listType, page: 1 });
      });
    }
  }
  componentDidUpdate(prevProps) {
    const { messageId, messageIds, listType, recommendFlixCategory, onChange } =
      this.props;
    if (
      messageIds !== prevProps.messageIds ||
      messageIds.length !== this.state.messageIds.length
    ) {
      this.timeout && clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.setState({ messageIds });
        onChange();
      });
    }

    const shouldFetchMessages =
      prevProps.listType !== listType ||
      (listType === 'relatedMessages' && prevProps.messageId !== messageId) ||
      (listType === 'feed' &&
        prevProps.recommendFlixCategory !== recommendFlixCategory);

    if (shouldFetchMessages) {
      this.fetchMessages({ type: listType, page: 1 });
    }
  }
  componentWillUnmount() {
    this.timeout && clearTimeout(this.timeout);
    if (!window.__IS_HYDRATED__) {
      window.__IS_HYDRATED__ = 'FlixDetailList';
    }
  }
  getCategory = ({ type }) => {
    const {
      outboxFeedNameWithQuery,
      messageId: relatedMessageId,
      recommendFlixCategory,
    } = this.props;
    switch (type) {
      case 'unlocked':
        return CustomImpression.FLIX_ARCHIVE;
      case 'outbox':
        return outboxFeedNameWithQuery;
      case 'feed':
        if (recommendFlixCategory) {
          return recommendFlixCategory;
        }
        return null;
      case 'relatedMessages':
        if (relatedMessageId) {
          return `message-${relatedMessageId}`;
        }
        return null;
      case 'pro':
        return freezoneFeed.MODEL;
      default:
        return null;
    }
  };
  fetchMessages = ({ type, page = 1 } = {}) => {
    const {
      outboxFeedNameWithQuery,
      userId,
      messageId: relatedMessageId,
      recommendFlixCategory,
      fetchUnlockedMessages,
      fetchFeeds,
    } = this.props;
    switch (type) {
      case 'unlocked':
        return fetchUnlockedMessages({
          page: page,
          senderId: userId,
          limit: 10,
          type: 'flix',
        });
      case 'outbox':
        return fetchFeeds({
          type: outboxFeedNameWithQuery,
        });
      case 'feed':
        if (recommendFlixCategory) {
          return fetchFeeds({
            page: page,
            limit: 10,
            type: recommendFlixCategory,
          });
        }
        return null;
      case 'relatedMessages':
        if (relatedMessageId) {
          return fetchFeeds({
            page: page,
            limit: 10,
            type: `message-${relatedMessageId}`,
          });
        }
        return null;
      case 'pro':
        return fetchFeeds({
          page: page,
          limit: 10,
          type: freezoneFeed.MODEL,
        });
      default:
        return null;
    }
  };

  renderHydrateFlix = (messageId, index) => {
    const { listType } = this.props;
    return (
      <ObservableHydrateFlixItem
        key={messageId}
        messageId={messageId}
        index={index}
        categoryId={this.getCategory({ type: listType })}
      />
    );
  };
  renderFlix = (messageId, index) => {
    const { shouldTrackRecommendEvent, listType } = this.props;
    return (
      <ObservableFlixItem
        key={messageId}
        messageId={messageId}
        index={index}
        categoryId={this.getCategory({ type: listType })}
        shouldTrackRecommendEvent={shouldTrackRecommendEvent}
      />
    );
  };
  loadMore = () => {
    const { listType, nextPage } = this.props;
    this.fetchMessages({ type: listType, page: nextPage });
  };
  renderFlixList = () => {
    const { messageIds } = this.state;
    const { isFetching, nextPage, listType } = this.props;
    const shouldShowPlaceholder =
      messageIds.length === 0 && listType === 'feed';
    const shouldHydrate = isServer || !window.__IS_HYDRATED__;
    const renderFlix = shouldHydrate ? this.renderHydrateFlix : this.renderFlix;

    return (
      <StyledFlixDetailList>
        <InfiniteScroller
          isFetching={isFetching}
          hasMore={nextPage !== null}
          useWindow={true}
          threshold={100}
          loadMore={() => {
            this.loadMore();
          }}
        >
          <ScrollBody isIphoneXSeriesPWA={getIsIphoneXSeriesPWA()}>
            <ScrollItem
              loader={
                <FlixListItemWrapper>
                  <FlixLoaderWrapper>
                    <Loading size={24} />
                  </FlixLoaderWrapper>
                </FlixListItemWrapper>
              }
            >
              {shouldShowPlaceholder
                ? new Array(10).fill(null).map((messageId, index) => (
                    <FlixListItemWrapper key={index}>
                      <FlixListItem
                        messageId={null}
                        shouldUseMobileLayout
                      ></FlixListItem>
                    </FlixListItemWrapper>
                  ))
                : messageIds.map(renderFlix)}
            </ScrollItem>
          </ScrollBody>
        </InfiniteScroller>
      </StyledFlixDetailList>
    );
  };
  render() {
    const { messageIds } = this.state;
    const { isFirstPageFetching } = this.props;
    if (isFirstPageFetching)
      return (
        <FlixLoaderWrapper>
          <Loading size={24} />
        </FlixLoaderWrapper>
      );
    return messageIds.length > 0 ? this.renderFlixList() : null;
  }
}

FlixDetailList.propTypes = {
  outboxFeedNameWithQuery: PropTypes.string,
  listType: PropTypes.string,
  userId: PropTypes.string,
  recommendFlixCategory: PropTypes.string,
  isFetching: PropTypes.bool,
  nextPage: PropTypes.number,
  messageIds: PropTypes.array,
  fetchUnlockedMessages: PropTypes.func,
  fetchFeeds: PropTypes.func,
  messageId: PropTypes.string,
  isFirstPageFetching: PropTypes.bool,
  isFirstPageFetched: PropTypes.bool,
  shouldTrackRecommendEvent: PropTypes.bool,
  onChange: PropTypes.func,
};

FlixDetailList.defaultProps = {
  outboxFeedNameWithQuery: null,
  listType: 'unlocked',
  userId: '',
  recommendFlixCategory: null,
  isFetching: null,
  nextPage: null,
  messageIds: [],
  fetchUnlockedMessages: () => null,
  fetchFeeds: () => null,
  messageId: null,
  isFirstPageFetching: false,
  isFirstPageFetched: false,
  shouldTrackRecommendEvent: false,
  onChange: () => null,
};

const IN_VIEW_OPTIONS_HYDRATE = { threshold: 0.2, triggerOnce: true };
const IN_VIEW_OPTIONS_FLIX = { triggerOnce: true };

const ObservableHydrateFlixItem = ({ messageId, index, categoryId }) => {
  const { ref, inView } = useInView(IN_VIEW_OPTIONS_HYDRATE);
  return (
    <HydrationBoundary
      shouldHydrate={inView}
      wrapper={<FlixListItemWrapper ref={ref} />}
    >
      <FlixListItem
        messageId={messageId}
        shouldUseLink
        shouldUseLazyImage={false}
        shouldUseMobileLayout
        id={messageId}
        categoryId={categoryId}
        itemIndexInCategory={index}
      />
    </HydrationBoundary>
  );
};

ObservableHydrateFlixItem.propTypes = {
  messageId: PropTypes.string,
  index: PropTypes.number,
  categoryId: PropTypes.string,
};

const ObservableFlixItem = ({
  messageId,
  index,
  categoryId,
  shouldTrackRecommendEvent,
}) => {
  const { ref, inView } = useInView(IN_VIEW_OPTIONS_FLIX);
  return (
    <FlixListItemWrapper ref={ref}>
      <FlixListItem
        shouldTrackRecommendEvent={shouldTrackRecommendEvent}
        shouldUseMobileLayout
        messageId={inView ? messageId : null}
        id={messageId}
        categoryId={categoryId}
        itemIndexInCategory={index}
      />
    </FlixListItemWrapper>
  );
};

ObservableFlixItem.propTypes = {
  messageId: PropTypes.string,
  index: PropTypes.number,
  categoryId: PropTypes.string,
  shouldTrackRecommendEvent: PropTypes.bool,
};

const StyledFlixDetailList = styled.div`
  background-color: #232323;
  padding: 40px 20px 20px 20px;
  @media (max-width: ${breakpoint.mobile}px) {
    padding: 16px;
  }
`;

const ScrollBody = styled.div``;

const FlixListItemWrapper = styled.div`
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(100, 100, 100, 0.5);
  margin-bottom: 8px;
  :last-child {
    border-bottom: none;
  }
`;

const FlixLoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 92px;
`;
export default withTranslation()(FlixDetailList);
